'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS, NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import MobileProductFilters from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/MobileProductFilters';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryAlgoliaSEO from './CategoryAlgoliaSEO';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import VirtualSearchBox from './VirtualSearchBox';
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(82);
  const {
    slug,
    serverCategoryData,
    facetFilters,
    selectedFacets,
    onToggleFacet,
    onClearFacets
  } = t0;
  const {
    isMobile
  } = useScreen();
  const {
    currentProductIndex,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  const {
    results,
    status
  } = useInstantSearch();
  const {
    query: parentQuery
  } = useSearchBox();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [enrichedProducts, setEnrichedProducts] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsSidebarOpen(_temp);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleSidebar = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = products => {
      setEnrichedProducts(products);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleProductsChange = t3;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t4 = categoryData?.pageId;
  let t5;
  if ($[3] !== enrichedProducts || $[4] !== pageName || $[5] !== t4) {
    t5 = {
      pageId: t4,
      pageName,
      products: enrichedProducts
    };
    $[3] = enrichedProducts;
    $[4] = pageName;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useCategoryAlgoliaAnalytics(t5);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const resultsCount = hasProductsLoaded && productsCount > 0 ? productsCount : null;
  const resultsCountText = resultsCount != null ? `${resultsCount} Products` : null;
  let filters;
  if ($[7] !== maxWidth || $[8] !== minWidth) {
    filters = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    $[7] = maxWidth;
    $[8] = minWidth;
    $[9] = filters;
  } else {
    filters = $[9];
  }
  const secondaryNumericFilters = filters;
  const isProductsIndex = currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS;
  let t6;
  if ($[10] !== categoryData) {
    t6 = <CategoryAlgoliaSEO categoryData={categoryData} />;
    $[10] = categoryData;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = categoryData?.pageLongName || pageName;
  const t8 = categoryData?.pageLongDescription || "";
  const t9 = categoryData?.pageId;
  const t10 = results?.nbHits;
  let t11;
  if ($[12] !== currentProductIndex || $[13] !== enrichedProducts || $[14] !== hasProductsLoaded || $[15] !== t10 || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t11 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t7} pageDescription={t8} pageId={t9} totalHits={t10} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[12] = currentProductIndex;
    $[13] = enrichedProducts;
    $[14] = hasProductsLoaded;
    $[15] = t10;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== pageName) {
    t13 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[21] = pageName;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== resultsCountText) {
    t14 = resultsCountText && <span className="text-sm text-gray-light2 font-normal">{resultsCountText}</span>;
    $[23] = resultsCountText;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t14) {
    t16 = <div className="md:flex md:gap-5 md:items-center">{t13}{t14}{t15}</div>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t18 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[29] = t17;
    $[30] = t18;
  } else {
    t17 = $[29];
    t18 = $[30];
  }
  let t19;
  if ($[31] !== isMobile || $[32] !== productsCount) {
    t19 = isMobile && <><MobileProductFilters productsCount={productsCount} /><div className="flex-1 min-w-[140px] flex items-center"><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp2)} /></div></>;
    $[31] = isMobile;
    $[32] = productsCount;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== isMobile) {
    t20 = !isMobile && <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp3)} /></div>;
    $[34] = isMobile;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t19 || $[37] !== t20) {
    t21 = <div className={t18}>{t19}{t20}</div>;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <MobileGridColumnToggle />;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t21) {
    t23 = <div className={t17}>{t21}{t22}</div>;
    $[40] = t21;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t16 || $[43] !== t23) {
    t24 = <div className={t12}>{t16}{t23}</div>;
    $[42] = t16;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const t25 = categoryData?.filters;
  let t26;
  if ($[45] !== onClearFacets || $[46] !== onToggleFacet || $[47] !== pageName || $[48] !== selectedFacets || $[49] !== t25) {
    t26 = <CategoryAlgoliaSidebar pageName={pageName} filters={t25} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} onClearFacets={onClearFacets} />;
    $[45] = onClearFacets;
    $[46] = onToggleFacet;
    $[47] = pageName;
    $[48] = selectedFacets;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== isSidebarOpen || $[52] !== t26) {
    t27 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t26}</DesktopProductListingSidebarContainer></div>;
    $[51] = isSidebarOpen;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== isProductsIndex) {
    t28 = isProductsIndex && <AdvancedSearchProductHits onProductsChange={handleProductsChange} />;
    $[54] = isProductsIndex;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== currentProductIndex || $[57] !== facetFilters || $[58] !== isProductsIndex || $[59] !== parentQuery || $[60] !== secondaryNumericFilters) {
    t29 = !isProductsIndex && <Index indexName={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={parentQuery} /><Configure facets={["*"]} facetFilters={facetFilters} numericFilters={secondaryNumericFilters} hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW} attributesToRetrieve={["_highlightResult", "product_id", "name", "is_in_stock"]} clickAnalytics={true} /><AdvancedSearchProductHits onProductsChange={handleProductsChange} /></Index>;
    $[56] = currentProductIndex;
    $[57] = facetFilters;
    $[58] = isProductsIndex;
    $[59] = parentQuery;
    $[60] = secondaryNumericFilters;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  const t30 = categoryData?.pageLongDescription || "";
  let t31;
  if ($[62] !== pageName || $[63] !== t30) {
    t31 = <ProductListingPageDescriptionShell pageLongDescription={t30} pageName={pageName} />;
    $[62] = pageName;
    $[63] = t30;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t28 || $[66] !== t29 || $[67] !== t31) {
    t32 = <div className="md:space-y-8 md:mb-8 md:flex-1">{t28}{t29}{t31}</div>;
    $[65] = t28;
    $[66] = t29;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t27 || $[70] !== t32) {
    t33 = <div className="md:flex md:flex-row md:gap md:mt-2">{t27}{t32}</div>;
    $[69] = t27;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  const t34 = categoryData?.pageId;
  let t35;
  if ($[72] !== t34) {
    t35 = <ProductListingPageWidgets categoryId={t34} />;
    $[72] = t34;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  let t36;
  if ($[74] !== t24 || $[75] !== t33 || $[76] !== t35) {
    t36 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t24}{t33}{t35}</FixedWidthContainer>;
    $[74] = t24;
    $[75] = t33;
    $[76] = t35;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t11 || $[79] !== t36 || $[80] !== t6) {
    t37 = <>{t6}{t11}{t36}</>;
    $[78] = t11;
    $[79] = t36;
    $[80] = t6;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  return t37;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object,
  facetFilters: PropTypes.array,
  selectedFacets: PropTypes.object,
  onToggleFacet: PropTypes.func,
  onClearFacets: PropTypes.func
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}
function _temp2(option) {
  return option.value !== "newest";
}
function _temp3(option_0) {
  return option_0.value !== "newest";
}